import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { UsersListItemDto } from "@green0meter/api-client";

import { FeatureUserRestrictionsProps } from "../../CompanyEditor.types";

import {
  TextField,
  Typography,
  Chip,
  Card,
  CardContent,
  Box,
  Stack,
  Autocomplete,
  AutocompleteRenderInputParams,
} from "@components/base";

const FeatureUserRestrictions: FC<FeatureUserRestrictionsProps> = ({
  selectedFeatures,
  availableFeatures,
  usersList,
  onUserIdsChange,
}) => {
  const { t } = useTranslation();

  const getSelectedUsersForFeature = (
    allowedUserIds: string[],
  ): UsersListItemDto[] => {
    return usersList.filter((u) => allowedUserIds.includes(u.id));
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("admin.featureUserRestrictions")}
        </Typography>
        <Stack spacing={2}>
          {selectedFeatures.map((feature) => {
            const featureInfo = availableFeatures.find(
              (f) => f.id === feature.applicationFeatureFunctionalityId,
            );

            const selectedUsers = getSelectedUsersForFeature(feature.allowedUserIds);

            return (
              <Box key={feature.applicationFeatureFunctionalityId}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                  {featureInfo?.label ?? feature.applicationFeatureFunctionalityId}
                </Typography>
                <Autocomplete<UsersListItemDto, true>
                  multiple
                  options={usersList}
                  getOptionLabel={(option: UsersListItemDto) =>
                    `${option.firstName} ${option.lastName} (${option.email})`
                  }
                  value={selectedUsers}
                  onChange={(_event, newValue) =>
                    onUserIdsChange(
                      feature.applicationFeatureFunctionalityId,
                      newValue,
                    )
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{
                    "& .MuiAutocomplete-inputRoot": {
                      minHeight: 56,
                      paddingTop: "4px",
                      paddingBottom: "4px",
                    },
                  }}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      variant="filled"
                      size="small"
                      placeholder={t("admin.selectAllowedUsers")}
                      helperText={t("admin.allowedUserIdsHelp")}
                    />
                  )}
                  renderTags={(value: UsersListItemDto[], getTagProps) =>
                    value.map((option: UsersListItemDto, index: number) => (
                      <Chip
                        label={`${option.firstName} ${option.lastName}`}
                        {...getTagProps({ index })}
                        key={option.id}
                        color="blue1"
                        size="small"
                        sx={{
                          m: 0.25,
                          height: 24,
                          "& .MuiChip-label": {
                            px: 1,
                          },
                          "& .MuiChip-deleteIcon": {
                            ml: 0,
                            mr: 0.5,
                          },
                        }}
                      />
                    ))
                  }
                />
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default FeatureUserRestrictions;
